import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Utilisateur, UtilisateurService } from '../../../shared';
import { BehaviorService } from '../../../shared/services/behavior/behavior.service';

@Component({
  selector: 'reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  user!: Utilisateur;
  loading!: boolean;
  showMailMsg: boolean = false;
  errorMsg: string | undefined;
  email!: string;

  resetPasswordInputType: 'text' | 'password' = 'password';
  isResetPasswordDisplayed = false;
  confirmResetPasswordInputType: 'text' | 'password' = 'password';
  isConfirmResetPasswordDisplayed = false;

  styleValidatorNumber: string = 'text-danger';
  styleValidatorUppercase: string = 'text-danger';
  styleValidatorLowercase: string = 'text-danger';
  styleValidatorSpecial: string = 'text-danger';
  styleValidatorSize: string = 'text-danger';

  resetPassForm = new FormGroup({
    email: new FormControl('', [Validators.required]),
    validationCode: new FormControl('', [Validators.required]),
    newPassword1: new FormControl('', [Validators.required]),
    newPassword2: new FormControl('', [Validators.required]),
  });

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly utilisateurService: UtilisateurService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly behaviorService: BehaviorService,
  ) {}

  ngOnInit() {
    this.loading = false;
    this.user = {} as Utilisateur;
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params['message'] == 'success') {
          this.showMailMsg = true;
        }
        if (params['email']) {
          this.email = params['email'];
          this.resetPassForm.controls.email.setValue(this.email);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public passwordChanges() {
    if (this.resetPassForm.value.newPassword1) {
      const redAlert: string = 'text-danger';
      const greenOk: string = 'text-success';

      const regNumber = new RegExp(/([0-9])/);
      if (regNumber.test(this.resetPassForm.value.newPassword1)) {
        this.styleValidatorNumber = greenOk;
      } else {
        this.styleValidatorNumber = redAlert;
      }

      const regUppercase = new RegExp(/([A-Z])/);
      if (regUppercase.test(this.resetPassForm.value.newPassword1)) {
        this.styleValidatorUppercase = greenOk;
      } else {
        this.styleValidatorUppercase = redAlert;
      }

      const reglowercase = new RegExp(/([a-z])/);
      if (reglowercase.test(this.resetPassForm.value.newPassword1)) {
        this.styleValidatorLowercase = greenOk;
      } else {
        this.styleValidatorLowercase = redAlert;
      }

      const regSpecial = new RegExp(/[#?!@$%^&*-.]/);
      if (regSpecial.test(this.resetPassForm.value.newPassword1)) {
        this.styleValidatorSpecial = greenOk;
      } else {
        this.styleValidatorSpecial = redAlert;
      }

      const regSize = new RegExp(/.{12,}$/);
      if (regSize.test(this.resetPassForm.value.newPassword1)) {
        this.styleValidatorSize = greenOk;
      } else {
        this.styleValidatorSize = redAlert;
      }
    }
  }

  public showResetPassword(): void {
    this.isResetPasswordDisplayed = !this.isResetPasswordDisplayed;
    this.resetPasswordInputType = this.isResetPasswordDisplayed
      ? 'text'
      : 'password';
  }

  public showConfirmResetPassword(): void {
    this.isConfirmResetPasswordDisplayed =
      !this.isConfirmResetPasswordDisplayed;
    this.confirmResetPasswordInputType = this.confirmResetPasswordInputType
      ? 'text'
      : 'password';
  }

  public async resetPassword() {
    this.loading = true;
    this.errorMsg = '';

    try {
      if (
        this.resetPassForm.value.validationCode &&
        this.resetPassForm.value.newPassword1 &&
        this.resetPassForm.value.newPassword2
      ) {
        await this.utilisateurService.resetPassword(
          this.email,
          this.resetPassForm.value.validationCode,
          this.resetPassForm.value.newPassword1,
          this.resetPassForm.value.newPassword2,
        );

        await this.router.navigate(['/connexion'], {
          queryParams: { message: 'reset-password' },
        });
      }
    } catch (err: any) {
      this.loading = false;
      this.errorMsg = err;
      this.showMailMsg = false;
    }

    this.loading = false;
    this.changeDetector.markForCheck();
    this.behaviorService.scrollToTheTop();
  }
}
